import { Button } from "components/DesignSystem/Button/Button";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Modal from "components/DesignSystem/Modal/Modal";
import { FileInput } from "components/FileInput/FileInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { DISPLAY_DATE } from "constants/date";
import { FinallyAddDetailsValidation } from "formValidations/FinallyAddDetailsValidation";
import { useToast } from "hooks/useToast";
import { useUploadFinallyFilesMutation } from "store/apis/finallyImport";
import { BackendError } from "types/utils/error";
import { FormikHelpers } from "formik";
import { FINALLY_FILE_ACCEPT, FINALLY_JE_ERROR_KEYWORD } from "./constants";
import { FinallyJournalEntriesInput } from "./FinallyJournalEntriesInput";
import { FinallyWhereToFindAccordion } from "./FinallyWhereToFindAccordion";
import {
  FinallyAddDetailsFormValues,
  useImportFinally,
} from "./ImportFinallyContext";
import { ImportFinallyHeader } from "./ImportFinallyHeader";

const DEFAULT_INITIAL_VALUES: FinallyAddDetailsFormValues = {
  date_till_import: "",
  prev_fy_end_date: "",
  coa_file: null,
  je_files: [],
};

export const FinallyAddDetailsStep = () => {
  const {
    entityId,
    onNext,
    setImportProcessId,
    addDetailsFormValues,
    setAddDetailsFormValues,
    isOpen,
    close,
  } = useImportFinally();
  const { alertToast } = useToast();
  const [uploadFinallyFiles] = useUploadFinallyFilesMutation();

  const initialValues = addDetailsFormValues || DEFAULT_INITIAL_VALUES;

  const handleSubmit = async (
    values: FinallyAddDetailsFormValues,
    formikHelpers?: FormikHelpers<FinallyAddDetailsFormValues>
  ) => {
    const { date_till_import, prev_fy_end_date, coa_file, je_files } = values;

    if (!coa_file || je_files.length === 0) return;

    try {
      const response = await uploadFinallyFiles({
        entityId,
        payload: { date_till_import, prev_fy_end_date, coa_file, je_files },
      }).unwrap();

      if (!response.coa_import_state_id) {
        alertToast({ message: "Something went wrong" });
        return;
      }

      setAddDetailsFormValues(values);
      setImportProcessId(response.coa_import_state_id);
      onNext();
    } catch (error) {
      const message =
        (error as BackendError).data?.error?.message ?? "Something went wrong";

      if (message.includes(FINALLY_JE_ERROR_KEYWORD) && formikHelpers) {
        formikHelpers.setFieldError("je_files", message);
        return;
      }

      alertToast({ message }, error as Error);
    }
  };

  return (
    <FormikForm<FinallyAddDetailsFormValues>
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={FinallyAddDetailsValidation}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, submitForm, isSubmitting, dirty }) => {
        const shouldTakeConfirmation = dirty || Boolean(addDetailsFormValues);

        const handleOpenChange = () =>
          close({ takeConfirmation: shouldTakeConfirmation });

        return (
          <Modal.Root
            open={isOpen}
            onOpenChange={handleOpenChange}
            modal={false}
          >
            <Modal.Content size="xl" useCustomOverlay>
              <ImportFinallyHeader />
              <Modal.Body className="t-flex t-flex-col t-gap-5">
                <div className="t-flex t-flex-col t-gap-2">
                  <div className="t-grid t-grid-cols-2 t-gap-4">
                    <DateField
                      name="prev_fy_end_date"
                      dateProps={{
                        tooltipText: (
                          <div className="t-text-center t-w-60">
                            Your last financial year's end date. Entries on or
                            before this date are skipped. Pick the day before
                            the first entry in your Journal Entries file.
                          </div>
                        ),
                        label: "From",
                        required: true,
                        placeholder: DISPLAY_DATE,
                      }}
                    />
                    <DateField
                      name="date_till_import"
                      dateProps={{
                        tooltipText: (
                          <div className="t-text-center t-w-60">
                            Entries up to and including this date will be
                            imported. Usually the last date in your Journal
                            Entries export.
                          </div>
                        ),
                        label: "To",
                        required: true,
                        placeholder: DISPLAY_DATE,
                      }}
                    />
                  </div>
                  <p className="t-m-0 t-text-body-sm t-text-text-30">
                    We import entries dated after the From date, up to the To
                    date. Opening balances are built from these entries, so no
                    balance sheet is needed. Existing transactions in this
                    period will be replaced.
                  </p>
                </div>
                <div className="t-text-md t-font-medium">
                  Documents
                  <FinallyWhereToFindAccordion />
                </div>
                <FileInput
                  label="Chart of Accounts"
                  withForm
                  name="coa_file"
                  accept={FINALLY_FILE_ACCEPT}
                  file={values.coa_file}
                  required
                />
                <FinallyJournalEntriesInput />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="reset" disabled={isSubmitting}>
                    Cancel
                  </Button>
                </Modal.RawClose>
                <Button
                  type="button"
                  customType="primary"
                  onClick={submitForm}
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  Next
                </Button>
              </Modal.FooterButtonGroup>
            </Modal.Content>
          </Modal.Root>
        );
      }}
    </FormikForm>
  );
};
